import { z } from 'zod';
import { assignmentSchema, issuanceFormSchema, issueLineRequestSchema } from './issuance.js';
/**
 * Bulk issue (§3.5, answered): the same items to every soldier in a unit,
 * one form per soldier, all reserved in one transaction or none.
 *
 * Lines carry no inventoryUnitId. For a serialized item the server takes
 * one in-stock unit per soldier, in serial order, and refuses the whole batch
 * if there are not enough — a half-equipped team is not an outcome anyone
 * asked for.
 */
export const MAX_BULK_SOLDIERS = 200;
/**
 * A Firestore transaction commits at most this many writes. A bulk issue is
 * one transaction on purpose (all or none), so the server counts the writes
 * it is about to make and refuses a batch that would not fit, naming the
 * number, rather than discovering it at commit.
 */
export const FIRESTORE_TRANSACTION_WRITE_LIMIT = 500;
/** The id the client sends for "the whole company"; there is no company unit document. */
export const COMPANY_TARGET = 'company';
export const bulkIssueRequestSchema = z
    .object({
    /**
     * A team, a platoon (every active soldier in every team beneath it), or
     * `COMPANY_TARGET` for everyone (owner, 2026-09-13; storeroom only).
     */
    unitId: z.string().min(1).optional(),
    /**
     * Or an explicit pick (owner, 2026-09-13): the rows selected on the org
     * grid, so one batch can go to some of a team and another batch to the
     * rest. Every uid must be active and within the caller's issue scope, or
     * the whole batch is refused.
     */
    soldierUids: z.array(z.string().min(1)).min(1).max(MAX_BULK_SOLDIERS).optional(),
    lines: z
        .array(issueLineRequestSchema.omit({ inventoryUnitId: true }))
        .min(1)
        .max(20),
    missionId: z.string().min(1).nullable().default(null),
    dueDate: z.string().datetime().nullable().default(null),
})
    .refine((b) => (b.unitId !== undefined) !== (b.soldierUids !== undefined), {
    message: 'Exactly one of unitId or soldierUids',
    path: ['unitId'],
});
export const bulkIssueResponseSchema = z.object({
    forms: z.array(issuanceFormSchema),
});
/**
 * GET /api/users/:uid/gear — everything a soldier currently holds, plus what
 * is pending their signature. The client splits standing from mission gear
 * by `missionId` (§2.5 "Missions", §2.6 "my gear split into standing and
 * mission").
 */
export const gearResponseSchema = z.object({
    assignments: z.array(assignmentSchema),
    /** Mission names for the ids that appear above, so the split has labels. */
    missions: z.array(z.object({ id: z.string(), name: z.string(), status: z.string() })),
});
//# sourceMappingURL=gear.js.map